import React from "react";
import { BrowserRouter, Route, Routes, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./lib/auth-context";
import { BrandColorProvider } from "./lib/brand-context";
import { SystemBrandingProvider } from "./lib/system-branding-context";
import { ThemeProvider, useTheme } from "./lib/theme-context";
import AppShell from "./components/layout/AppShell";
import LoginPage from "./pages/LoginPage";
import DashboardPage from "./pages/DashboardPage";
import LeadsPage from "./pages/LeadsPage";
import LeadDetailPage from "./pages/LeadDetailPage";
import CompaniesPage from "./pages/CompaniesPage";
import CompanyDetailPage from "./pages/CompanyDetailPage";
import ContactsPage from "./pages/ContactsPage";
import ContactDetailPage from "./pages/ContactDetailPage";
import DealDetailPage from "./pages/DealDetailPage";
import PipelinePage from "./pages/PipelinePage";
import TasksPage from "./pages/TasksPage";
import TaskDetailPage from "./pages/TaskDetailPage";
import ActivitiesPage from "./pages/ActivitiesPage";
import EmailsPage from "./pages/EmailsPage";
import ConsentsPage from "./pages/ConsentsPage";
import SettingsPage from "./pages/SettingsPage";
import TeamPage from "./pages/TeamPage";
import FormsPage from "./pages/FormsPage";
import SuperAdminTenantsPage from "./pages/SuperAdminTenantsPage";
import PublicDemoPage from "./pages/PublicDemoPage";
import PublicThankYouPage from "./pages/PublicThankYouPage";
import PublicConsentPage from "./pages/PublicConsentPage";
import InviteAcceptPage from "./pages/InviteAcceptPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import { canAccessPublicForms, canSeeMailAudit } from "./lib/capabilities";
import { canSeeConsentCenter, canSeeEmailLog, canSeeFormsNavigation } from "./lib/crm-permissions";
import "./App.css";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center text-sm text-muted-foreground">
        Wird geladen …
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function SuperAdminRoute({ children }) {
  const { loading, isSuperAdmin } = useAuth();
  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center text-sm text-muted-foreground">
        Wird geladen ...
      </div>
    );
  }
  if (!isSuperAdmin) return <Navigate to="/dashboard" replace />;
  return children;
}

function EmailLogRoute({ children }) {
  const { loading, role, isSuperAdmin, capabilities } = useAuth();
  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center text-sm text-muted-foreground">
        Wird geladen ...
      </div>
    );
  }
  if (!canSeeEmailLog({ role, isSuperAdmin }) || !canSeeMailAudit(capabilities)) {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
}

function ConsentCenterRoute({ children }) {
  const { loading, role, isSuperAdmin } = useAuth();
  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center text-sm text-muted-foreground">
        Wird geladen ...
      </div>
    );
  }
  if (!canSeeConsentCenter({ role, isSuperAdmin })) return <Navigate to="/dashboard" replace />;
  return children;
}

function FormsRoute({ children }) {
  const { loading, role, isSuperAdmin, capabilities } = useAuth();
  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center text-sm text-muted-foreground">
        Wird geladen ...
      </div>
    );
  }
  if (!canSeeFormsNavigation({ role, isSuperAdmin }) || !canAccessPublicForms(capabilities)) {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
}

function RootRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/invite" element={<InviteAcceptPage />} />
      <Route path="/passwort-vergessen" element={<ForgotPasswordPage />} />
      <Route path="/passwort-zuruecksetzen" element={<ResetPasswordPage />} />
      <Route path="/einwilligungen/:token" element={<PublicConsentPage />} />
      <Route path="/demo/:formKey" element={<PublicDemoPage />} />
      <Route path="/demo/:formKey/danke" element={<PublicThankYouPage />} />

      <Route
        element={
          <ProtectedRoute>
            <BrandColorProvider>
              <AppShell />
            </BrandColorProvider>
          </ProtectedRoute>
        }
      >
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/leads" element={<LeadsPage />} />
        <Route path="/leads/:id" element={<LeadDetailPage />} />
        <Route path="/firmen" element={<CompaniesPage />} />
        <Route path="/firmen/:id" element={<CompanyDetailPage />} />
        <Route path="/kontakte" element={<ContactsPage />} />
        <Route path="/kontakte/:id" element={<ContactDetailPage />} />
        <Route path="/pipeline" element={<PipelinePage />} />
        <Route path="/deals/:id" element={<DealDetailPage />} />
        <Route path="/aufgaben" element={<TasksPage />} />
        <Route path="/aufgaben/:id" element={<TaskDetailPage />} />
        <Route path="/aktivitaeten" element={<ActivitiesPage />} />
        <Route
          path="/formulare"
          element={(
            <FormsRoute>
              <FormsPage />
            </FormsRoute>
          )}
        />
        <Route
          path="/einwilligungen"
          element={(
            <ConsentCenterRoute>
              <ConsentsPage />
            </ConsentCenterRoute>
          )}
        />
        <Route
          path="/emails"
          element={(
            <EmailLogRoute>
              <EmailsPage />
            </EmailLogRoute>
          )}
        />
        <Route path="/team" element={<TeamPage />} />
        <Route path="/einstellungen" element={<SettingsPage />} />
        <Route
          path="/admin/tenants"
          element={(
            <SuperAdminRoute>
              <SuperAdminTenantsPage />
            </SuperAdminRoute>
          )}
        />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function ThemedToaster() {
  const { isDark } = useTheme();
  return (
    <Toaster
      richColors
      position="top-right"
      theme={isDark ? "dark" : "light"}
      toastOptions={{
        style: {
          background: "hsl(var(--card))",
          color: "hsl(var(--card-foreground))",
          border: "1px solid hsl(var(--border))",
        },
      }}
    />
  );
}

function App() {
  return (
    <ThemeProvider>
      <BrowserRouter>
        <AuthProvider>
          <SystemBrandingProvider>
            <RootRoutes />
            <ThemedToaster />
          </SystemBrandingProvider>
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
